import { isResponseError } from '@repo/upfetch'

export type ApiErrorMessageEntry = {
  id?: string
  message: string
  title?: string
}

type ApiErrorData = {
  errors?: Record<string, string | string[]>
  message?: string
}

/**
 * Extract a human-readable error message from an API error.
 *
 * - If the error is a response error from `up-fetch` and contains a validation
 *   `errors` object, format the field messages into structured entries.
 * - Otherwise fall back to `data.message` or `error.message` when available.
 * - If nothing is available, return the provided fallback as a single entry.
 */
export function getApiErrorMessage(
  error: unknown,
  fallback = 'An unexpected error occurred. Please try again.',
): ApiErrorMessageEntry[] {
  if (isResponseError<ApiErrorData | undefined>(error)) {
    const data = error.data
    if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
      const formatted = formatValidationErrors(data.errors)
      if (formatted.length > 0) return formatted
    }
    const message = data?.message
    if (typeof message === 'string' && message.length > 0) return [{ message }]
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    const msg = error.message
    if (msg) return [{ message: msg }]
  }

  return [{ message: fallback }]
}

function formatValidationErrors(errors: Record<string, string | string[]>): ApiErrorMessageEntry[] {
  const entries: ApiErrorMessageEntry[] = []
  for (const [key, messages] of Object.entries(errors)) {
    const normalizedKey = normalizeFieldName(key)
    const msgs = Array.isArray(messages) ? messages : [messages]
    for (let i = 0; i < msgs.length; i++) {
      const msg = msgs[i]
      if (typeof msg === 'string' && msg.length > 0) {
        entries.push({
          id: `${key}-${i}`,
          message: msg,
          title: normalizedKey,
        })
      }
    }
  }
  return entries
}

function normalizeFieldName(key: string): string {
  const withSpaces = key.replace(/[_\-.]/g, ' ')
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1)
}
